'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, CreditCard, Info, Loader2, Smartphone, XCircle } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { PaymentStatusBadge } from '@/components/payments/payment-status-badge';
import { formatMoney } from '@/lib/money';
import { cn } from '@/lib/utils';

interface RetryPaymentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  instruments: any[];
  payment: any;
  isLoading: boolean;
  onSelectInstrument: (instrumentId: string) => void;
  /** Charge the instrument the last retry used even though it has no reusable credential. */
  onForce: () => void;
  step: 'select' | 'result';
}

const isApplePay = (instrument: any) => instrument.type === 'apple_pay';

const brandOf = (instrument: any): string | null => {
  const brand = instrument.card?.brand || instrument.cardEnrichment?.brand;
  if (!brand) return null;
  return brand
    .split(/[-_ ]/)
    .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const last4Of = (instrument: any): string | null =>
  instrument.card?.last4 || instrument.cardEnrichment?.last4 || null;

const expiryOf = (instrument: any): string | null => {
  const month = instrument.card?.exp_month ?? instrument.cardEnrichment?.expirationMonth;
  const year = instrument.card?.exp_year ?? instrument.cardEnrichment?.expirationYear;
  if (!month || !year) return null;
  return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`;
};

function InstrumentLine({ instrument }: { instrument: any }) {
  const { t } = useTranslation('orders');
  const applePay = isApplePay(instrument);
  const brand = brandOf(instrument);
  const last4 = last4Of(instrument);
  const expiry = expiryOf(instrument);
  const Icon = applePay ? Smartphone : CreditCard;
  return (
    <div className="flex min-w-0 items-center gap-3">
      <div className="bg-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
        <Icon className="text-muted-foreground h-4 w-4" />
      </div>
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium">
          {applePay ? 'Apple Pay' : brand ?? 'Card'}
          {applePay && brand ? <span className="text-muted-foreground font-normal"> · {brand}</span> : null}
        </span>
        <span className="text-muted-foreground truncate text-xs">
          {last4 ? t('cardEnding', { last4 }) : null}
          {last4 && expiry ? ' · ' : null}
          {expiry ? t('expires', { date: expiry }) : null}
          {applePay ? <span className="block">{t('applePayNotReusable')}</span> : null}
        </span>
      </div>
    </div>
  );
}

function ResultIcon({ payment }: { payment: any }) {
  if (payment.status === 'succeeded') return <CheckCircle2 className="h-9 w-9 text-green-600" />;
  if (payment.requireAction) return <AlertTriangle className="h-9 w-9 text-amber-500" />;
  if (['declined', 'error', 'rejected'].includes(payment.status)) return <XCircle className="h-9 w-9 text-red-600" />;
  return <Info className="h-9 w-9 text-blue-500" />;
}

export function RetryPaymentDialog({
  isOpen,
  onClose,
  instruments,
  payment,
  isLoading,
  onSelectInstrument,
  onForce,
  step,
}: RetryPaymentDialogProps) {
  const { t } = useTranslation('orders');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [forceConfirmed, setForceConfirmed] = useState(false);

  // A fresh dialog starts unconfirmed, and so does every new result.
  useEffect(() => {
    if (!isOpen) setSelectedId(null);
    setForceConfirmed(false);
  }, [isOpen, payment?.id]);

  const noStoredCredential = payment?.status === 'rejected' && payment?.error?.code === 'no_stored_credential';

  const statusMessage = (() => {
    if (!payment) return '';
    if (payment.status === 'succeeded') return t('paymentSucceeded');
    if (payment.status === 'pending') return t('paymentPending');
    if (payment.status === 'declined') return t('paymentDeclined');
    if (noStoredCredential) return t('paymentNoStoredCredential');
    if (payment.status === 'error' || payment.status === 'rejected') return t('paymentError');
    return String(payment.status);
  })();

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isLoading) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{step === 'select' ? t('selectPaymentMethod') : t('paymentResult')}</DialogTitle>
          {step === 'select' && <DialogDescription>{t('selectPaymentMethodDescription')}</DialogDescription>}
        </DialogHeader>

        {step === 'select' && (
          <RadioGroup value={selectedId ?? ''} onValueChange={setSelectedId} className="space-y-2 py-2">
            {instruments.map((instrument) => {
              const disabled = isLoading || isApplePay(instrument);
              return (
                <Label
                  key={instrument.id}
                  htmlFor={instrument.id}
                  className={cn(
                    'flex items-center gap-3 rounded-lg border p-3 transition-colors',
                    disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-muted/50 cursor-pointer',
                    selectedId === instrument.id && 'border-primary bg-primary/5',
                  )}
                >
                  <RadioGroupItem value={instrument.id} id={instrument.id} disabled={disabled} />
                  <InstrumentLine instrument={instrument} />
                </Label>
              );
            })}
          </RadioGroup>
        )}

        {step === 'result' && payment && (
          <div className="space-y-4 py-2">
            <div className="flex flex-col items-center gap-3 text-center">
              <ResultIcon payment={payment} />
              <p className="text-base font-semibold leading-snug">{statusMessage}</p>
            </div>

            <dl className="divide-y rounded-lg border text-sm">
              <div className="flex items-center justify-between px-3 py-2">
                <dt className="text-muted-foreground">Status</dt>
                <dd>
                  <PaymentStatusBadge status={payment.status} subStatus={payment.subStatus} />
                </dd>
              </div>
              {payment.amount && payment.currency ? (
                <div className="flex items-center justify-between px-3 py-2">
                  <dt className="text-muted-foreground">{t('amount')}</dt>
                  <dd className="font-medium">{formatMoney(Number(payment.amount), String(payment.currency))}</dd>
                </div>
              ) : null}
              <div className="flex items-center justify-between px-3 py-2">
                <dt className="text-muted-foreground">Payment ID</dt>
                <dd className="font-mono text-xs">{payment.id}</dd>
              </div>
            </dl>

            {noStoredCredential && (
              <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="space-y-3">
                  <p className="text-sm">{t('noStoredCredentialExplain')}</p>
                  <label className="flex cursor-pointer items-start gap-2 text-sm font-medium">
                    <Checkbox
                      checked={forceConfirmed}
                      onCheckedChange={(v) => setForceConfirmed(v === true)}
                      disabled={isLoading}
                      className="mt-0.5"
                    />
                    <span>{t('forceConfirm')}</span>
                  </label>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {step === 'select' ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={isLoading}>
                {t('cancel')}
              </Button>
              <Button
                onClick={() => selectedId && onSelectInstrument(selectedId)}
                disabled={!selectedId || isLoading}
                className="min-w-[140px]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('processingPayment')}
                  </>
                ) : (
                  t('retryPayment')
                )}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={isLoading}>
                {t('common:close', { ns: 'common' })}
              </Button>
              {noStoredCredential && (
                <Button
                  variant="destructive"
                  onClick={onForce}
                  disabled={!forceConfirmed || isLoading}
                  className="min-w-[140px]"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('processingPayment')}
                    </>
                  ) : (
                    t('forcePayment')
                  )}
                </Button>
              )}
              {payment?.requireAction === 'redirect' && payment?.requireActionData?.url && (
                <Button onClick={() => window.open(String(payment.requireActionData.url), '_blank')}>
                  Complete Payment
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
